import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { shallowEqual } from "react-redux";
import { fetchTiposAprendizaje } from "../Slices/TipoAprendizaje/tipoAprendizajeSlice";
import { fetchCategorias } from "../Slices/Categoria/categoriaSlice";
import { fetchIdiomas } from "../Slices/Idioma/idiomaSlice";
import { fetchDificultades } from "../Slices/Dificultad/dificultadSlice";
import { storageService } from "Services/storage_service";

/**
 * Hook personalizado para obtener datos de filtros
 * Verifica si hay data en Redux antes de hacer fetch
 * Filtra automáticamente por estado activo (estado === 1)
 *
 * @returns {Object} { filterData, isLoading, refetch, rawData }
 */
export const useFilterData = () => {
  const dispatch = useDispatch();
  const user = storageService.getItem("user");

  const tiposAprendizaje = useSelector(
    (state) => state.tipoAprendizaje.tipoAprendizajes,
    shallowEqual
  );
  const categorias = useSelector(
    (state) => state.categoria.categorias,
    shallowEqual
  );
  const idiomas = useSelector((state) => state.idioma.idiomas, shallowEqual);
  const dificultades = useSelector(
    (state) => state.dificultad.dificultades,
    shallowEqual
  );

  const needsFetchTipos = !tiposAprendizaje || tiposAprendizaje.length === 0;
  const needsFetchCategorias = !categorias || categorias.length === 0;
  const needsFetchIdiomas = !idiomas || idiomas.length === 0;
  const needsFetchDificultades = !dificultades || dificultades.length === 0;

  const refetch = async () => {
    try {
      await Promise.all([
        dispatch(fetchTiposAprendizaje()),
        dispatch(fetchCategorias({ codSegmento: "capa" })),
        dispatch(fetchIdiomas()),
        dispatch(fetchDificultades()),
      ]);
    } catch (error) {
      console.error("Error fetching filter data:", error);
    }
  };

  useEffect(() => {
    const loadMissingData = async () => {
      const fetchPromises = [];

      if (needsFetchTipos) {
        fetchPromises.push(dispatch(fetchTiposAprendizaje()));
      }
      if (needsFetchCategorias) {
        fetchPromises.push(
          dispatch(fetchCategorias({ codSegmento: "capa"}))
        );
      }
      if (needsFetchIdiomas) {
        fetchPromises.push(dispatch(fetchIdiomas()));
      }
      if (needsFetchDificultades) {
        fetchPromises.push(dispatch(fetchDificultades()));
      }

      if (fetchPromises.length > 0) {
        try {
          await Promise.all(fetchPromises);
        } catch (error) {
          console.error("Error loading filter data:", error);
        }
      }
    };

    loadMissingData();
  }, [
    dispatch,
    needsFetchTipos,
    needsFetchCategorias,
    needsFetchIdiomas,
    needsFetchDificultades,
    user?.codSegmento,
  ]);

  const filterData = useMemo(() => {
    const filterByEstado = (data) =>
      data?.filter((item) => item.estado === 1) || [];

    return {
      tipos: filterByEstado(tiposAprendizaje),
      temas: filterByEstado(categorias),
      idiomas: filterByEstado(idiomas),
      dificultad: filterByEstado(dificultades),
    };
  }, [tiposAprendizaje, categorias, idiomas, dificultades]);

  const isLoading = useMemo(() => {
    return (
      needsFetchTipos ||
      needsFetchCategorias ||
      needsFetchIdiomas ||
      needsFetchDificultades
    );
  }, [
    needsFetchTipos,
    needsFetchCategorias,
    needsFetchIdiomas,
    needsFetchDificultades,
  ]);

  return {
    filterData,
    isLoading,
    refetch,
    rawData: {
      tiposAprendizaje,
      categorias,
      idiomas,
      dificultades,
    },
  };
};
